import React, { Suspense, Component, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface LazyPageWrapperProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

// Enhanced loading component with skeleton UI
const PageLoadingSkeleton = () => (
  <div className="min-h-screen bg-background">
    <div className="container mx-auto px-4 py-8">
      {/* Header skeleton */}
      <div className="mb-8">
        <div className="h-8 bg-muted rounded-md w-1/3 mb-4 animate-pulse"></div>
        <div className="h-4 bg-muted rounded-md w-2/3 animate-pulse"></div>
      </div>
      
      {/* Content skeleton */}
      <div className="grid gap-6">
        <div className="h-32 bg-muted rounded-lg animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-24 bg-muted rounded-lg animate-pulse"></div>
          <div className="h-24 bg-muted rounded-lg animate-pulse"></div>
        </div>
        <div className="h-48 bg-muted rounded-lg animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Error boundary for chunk loading failures
class ChunkLoadingErrorBoundary extends Component<
  { children: ReactNode; onRetry: () => void },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode; onRetry: () => void }) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, retryCount: 0 };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chunk loading error:', error, errorInfo);
    
    // Track chunk loading errors for monitoring
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as { gtag?: (...args: unknown[]) => void }).gtag;
      if (typeof gtag === 'function') {
        gtag('event', 'chunk_loading_error', {
          error_message: error.message,
          error_stack: error.stack,
          retry_count: this.state.retryCount
        });
      }
    }
  }

  handleRetry = () => {
    const newRetryCount = this.state.retryCount + 1;
    
    if (newRetryCount <= 3) {
      this.setState({ 
        hasError: false, 
        error: undefined, 
        retryCount: newRetryCount 
      });
      this.props.onRetry();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-semibold mb-2">Page Loading Error</h2>
            <p className="text-muted-foreground mb-6">
              There was a problem loading this page. This might be due to a network issue or a temporary problem.
            </p>
            
            {this.state.retryCount < 3 ? (
              <Button onClick={this.handleRetry} className="mr-2">
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry ({3 - this.state.retryCount} attempts left)
              </Button>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Maximum retry attempts reached. Please try refreshing the page.
                </p>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            )}
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Main LazyPageWrapper component
export const LazyPageWrapper: React.FC<LazyPageWrapperProps> = ({ 
  children, 
  fallback = <PageLoadingSkeleton /> 
}) => {
  const handleRetry = () => {
    // Force a re-render by updating the key
    window.location.reload();
  };

  return (
    <ChunkLoadingErrorBoundary onRetry={handleRetry}>
      <Suspense fallback={fallback}>
        {children}
      </Suspense>
    </ChunkLoadingErrorBoundary>
  );
};

export default LazyPageWrapper;